import { useApiQuery } from "@gladis/api-client";
import type { CurrentUserDto } from "@gladis/shared";
import { createFileRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import { AuthPage } from "../components/auth-page.js";
import { useAuth } from "../lib/auth-context.js";
import { ErrorState, LoadingState } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";

/**
 * The guard, as a PATHLESS layout route.
 *
 * `_authenticated` contributes no URL segment, so its children mount at the paths their filenames
 * imply — but they render inside this component. That is what makes protection a property of WHERE
 * A FILE LIVES rather than of a list of paths someone has to remember to update.
 *
 * Two rules, both written as "any in-app URL" in the spec:
 *
 *   no session            -> /sign-in, preserving where they were going  (FR-012)
 *   session, no household -> /onboarding                                 (FR-016)
 *
 * The catch-all lives under this layout too, so an unknown URL is guarded rather than rendering
 * outside the tree.
 */
export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});

function AuthenticatedLayout(): ReactNode {
  const t = useT();
  const { isAuthenticated, isRestoring } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const me = useApiQuery<CurrentUserDto>(["me"], "/me", { enabled: isAuthenticated });

  useEffect(() => {
    // On a cold load the access token is being renewed from the refresh cookie. Redirecting before
    // that settles bounces a signed-in person to sign-in on every reload.
    if (isRestoring) return;

    if (!isAuthenticated) {
      const next = `${window.location.pathname}${window.location.search}`;
      void navigate({ to: "/sign-in", search: { next }, replace: true });
      return;
    }
    if (me.data && me.data.activeHousehold === null && pathname !== "/onboarding") {
      void navigate({ to: "/onboarding", replace: true });
    }
  }, [isRestoring, isAuthenticated, me.data, pathname, navigate]);

  // Centred like the entry screens rather than pinned to the top of the page: this is what stands
  // in for sign-in and onboarding while they load, so it should not jump when they replace it.
  if (isRestoring || !isAuthenticated || me.isLoading)
    return (
      <AuthPage>
        <LoadingState />
      </AuthPage>
    );
  if (me.isError)
    return (
      <AuthPage>
        <ErrorState>{t("shell.accountLoadFailed")}</ErrorState>
      </AuthPage>
    );

  return <Outlet />;
}
